import { z } from 'zod';
import type { GymBox, PlatformDef } from '../level/gym.ts';
// The BASE constant fill has exactly one owner (`src/render/indirect.ts` — the module that also
// owns `hemi.intensity`), so the schema's default is IMPORTED rather than re-typed. A second
// literal `1.25` in this file is how "no shipped default changes" quietly stops being true.
// `indirect.ts` imports nothing at all, so this costs the emitter no dependency.
import { AMBIENT_FILL_BASE } from '../render/indirect.ts';
// Same reason as AMBIENT_FILL_BASE above: the density-level ceiling is IMPORTED from the module
// that owns it (`probeBrick.ts` — dependency-free constants) rather than re-typed as a literal 4.
// A second `4` in this file is how the schema and the compiler quietly stop agreeing on the ladder.
import { MAX_LEVEL } from '../render/gi/probeBrick.ts';
// And again for the light vocabulary (E2): the type/mobility enums and the (type × mobility)
// support table have ONE owner, `src/render/lights/lightModel.ts` (pure, imports nothing), and the
// schema spells its enums from those tuples rather than repeating the strings. A world file that
// could name a mobility the renderer has never heard of is the same class of bug as a world file
// that could name a density level the compiler cannot build.
import {
  APPLIED_SHADOW_UPDATES,
  DEFAULT_SHADOW_UPDATE_N,
  LIGHT_MOBILITIES,
  LIGHT_TYPES,
  SHADOW_UPDATES,
} from '../render/lights/lightModel.ts';
import { SurfaceProfileSchema, type SurfaceMap, type SurfaceProfile } from '../sim/tuning.ts';

/**
 * World manifest schema — `axon.world/1` (E0.5, ADR-0001). A world is DATA: it declares spawn,
 * geometry, surface-palette overrides and entity placements, and references runtime classes by
 * NAME (`requires`); it never contains code. This module is PURE (zod only, no three/DOM) — it is
 * imported by the runtime, the headless tests AND the Node emitter
 * (pipeline/authoring/emit-world.ts), which is why its relative imports carry explicit `.ts`
 * extensions (Node --experimental-strip-types resolves them literally).
 *
 * `entities` stays `unknown[]` here on purpose: each placement is validated per-entry by the
 * ENTITY registry's own schemas at spawn time (src/entities/host.ts) — the world schema does not
 * duplicate that vocabulary.
 */

const Vec3Schema = z.tuple([z.number(), z.number(), z.number()]);

/** Mobility vocabulary (rendering plan §4.1 — UE terms). Absent on a box ⇒ 'static'. */
export const MobilitySchema = z.enum(['static', 'stationary', 'dynamic']);
export type Mobility = z.infer<typeof MobilitySchema>;

/** Mirrors GymBox (src/level/gym.ts) — the compile-time assertions below keep them locked. */
export const WorldBoxSchema = z.object({
  pos: Vec3Schema,
  size: Vec3Schema,
  rotZDeg: z.number().optional(),
  station: z.string(),
  surface: z.string().optional(),
  /**
   * Rendering mobility (plan §4.1): static (batched, matrix written once) | stationary (doors,
   * breakables — batched but shadow-invalidating) | dynamic (own mesh, auto-updating matrix).
   * OPTIONAL, absent ⇒ 'static' (deliberately not zod-defaulted so emitted worlds don't grow a
   * field per box — use `mobilityOf`). Informational in R1: the runtime's one consumer today is
   * the boot matrixAutoUpdate policy; the cell batcher consumes it for real in A4.
   */
  mobility: MobilitySchema.optional(),
});

/** Effective mobility of a world box — the schema keeps the field optional (absent = static). */
export function mobilityOf(box: { mobility?: Mobility }): Mobility {
  return box.mobility ?? 'static';
}

/**
 * Mirrors PlatformDef (src/level/gym.ts). No `mobility` field: moving platforms are inherently
 * 'stationary' in the plan §4.1 vocabulary (batched-but-moving; excluded from HLOD merges) —
 * a per-def flag would only invite contradictions.
 */
export const WorldPlatformSchema = z.object({
  kind: z.enum(['linear', 'circular', 'elevator']),
  center: Vec3Schema,
  size: Vec3Schema,
  periodS: z.number().positive(),
  amplitude: z.number(),
  station: z.string(),
});

/** Per-tag PARTIAL override of a surface profile — merged over the content palette. */
export const SurfaceOverrideSchema = z.object({
  frictionMul: z.number().positive().max(2).optional(),
  maxSpeedMul: z.number().positive().max(2).optional(),
  cadenceMul: z.number().positive().max(2).optional(),
});

/**
 * World-level extension of a framework class (ADR-0002 tier b, "prefab"): PURE DATA — a name
 * that `extends` a registry class (or another prefab) with param overrides, resolved at load by
 * src/world/prefabs.ts. Prefabs never carry code and never appear in `requires` (only resolved
 * BASE classes do — prefab names are world-internal).
 */
export const WorldPrefabSchema = z.object({
  /** Base class name — a registry framework class, or another prefab in this world (chain). */
  extends: z.string(),
  /** Param overrides merged under entity-level fields (see resolveEntityDef precedence). */
  params: z.record(z.string(), z.unknown()).default({}),
});

/**
 * ⭐ THE PLACEMENT ROTATION FRAGMENT — ONE DEFINITION, SPREAD BY EVERY PLACEABLE CLASS.
 *
 * Before this, SEVEN entity schemas each declared `yawDeg: z.number().default(0)` independently
 * (`registry.ts` ×2, `exploration/table.ts` ×4, `menu/table.ts` ×1). That duplication is why
 * "placements are yaw-only" was a seven-place fact rather than one, and why widening it looked
 * like seven changes instead of a single shared shape. It is a fragment rather than a base schema
 * because the registry's entries are `z.object`s whose `type` literal discriminates them — spread,
 * not extend.
 *
 * ⚠️ WHY THIS EXISTS AT ALL: a real UE level needs it. The Mars census found **720 of 5,564
 * placements (12.9 %) carrying non-zero pitch or roll** — 572 roll-only, 128 pitch-only, 20 both.
 * Yaw-only does not fail loudly on those; it lays them flat, which reads as "the level looks a bit
 * wrong" and never as an error.
 *
 * ⭐ ADDITIVE, SO NOT ONE `worldHash` MOVES — the same argument `scale` and `collision` rest on:
 * `WorldSchema.entities` is `z.array(z.unknown())`, so placements are stored RAW and these
 * `.default(0)`s are materialised at VALIDATION time, never written back into `world.json`. Every
 * existing world hashes exactly as before, and the baked GI atlases on disk stay valid.
 *
 * ⚠️ ROTATION ORDER IS NOT A FREE CHOICE — see `ROTATION_ORDER` in `world/collision.ts`. These are
 * UE rotator components by name and by meaning, and the order they compose in is derived there,
 * once, rather than assumed at each application site.
 */
export const PLACEMENT_ROTATION = {
  /** Yaw about +Y (up), degrees. 0 = game forward (−Z). The historical spelling; unchanged. */
  yawDeg: z.number().default(0),
  /** Pitch, degrees — UE's rotator pitch, about the placement's right axis. */
  pitchDeg: z.number().default(0),
  /** Roll, degrees — UE's rotator roll, about the placement's forward axis. */
  rollDeg: z.number().default(0),
} as const;

/** The fixed vocabulary of world-scoped asset kinds (ADR-0002 tier c). */
export const WorldAssetKindSchema = z.enum(['mesh', 'texture', 'material', 'audio']);
export type WorldAssetKind = z.infer<typeof WorldAssetKindSchema>;

const KEBAB = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const AssetId = z.string().regex(KEBAB, 'asset id must be kebab-case (a-z, 0-9, dashes)');

/**
 * File-backed world asset (ADR-0002 tier c): a mesh/texture/audio file the world's data bundle
 * carries, referenced by `uri`. MANIFEST ONLY for now — the runtime loader IGNORES this table
 * (no fetching); the bundle pipeline (A4) produces and consumes these entries (content-hashed
 * asset buckets). The editor's WORLD ASSETS browser root is their visible landing zone today.
 */
export const WorldFileAssetSchema = z.object({
  /** Kebab-case id, unique within this world's assets table. */
  id: AssetId,
  kind: z.enum(['mesh', 'texture', 'audio']),
  /** Where the asset lives (bundle-relative path or URL) — not fetched by this runtime yet. */
  uri: z.string(),
  description: z.string().optional(),
  /**
   * ⭐ THE ASSET'S COLLISION DECLARATION (EDITOR v2 · S5, `world/collision.ts`): `ucx` = the
   * `UCX_/UBX_/USP_/UCP_` nodes inside the GLB are the colliders (stripped from the render graph);
   * `trimesh` = every render primitive, exact; `box` = the asset's AABB; `none` = walk-through.
   * A placement may override it (`StaticMeshSchema.collision`); `resolveCollisionMode` is the
   * ONE resolver and it reports which of the two answered.
   *
   * `.optional()` with NO `.default()`, and that is load-bearing: `worldHash` hashes the PARSED
   * world and `canonicalJson` drops `undefined`, so an absent field leaves every existing world's
   * hash — and its baked atlas — exactly where it was (the `lights` precedent above). ABSENT MEANS
   * `trimesh` for a mesh asset (B2): Sponza is solid with zero bytes changed in its `world.json`.
   * A `.default('trimesh')` here would have orphaned every bake on disk to say the same thing.
   */
  collision: z.enum(['none', 'ucx', 'trimesh', 'box']).optional(),
  /**
   * ⭐ M3 · MESH ONLY — THE WORLD MATERIAL EACH OF THIS MESH'S SLOTS BINDS TO, IN SLOT ORDER.
   *
   * A GLB exported "materials off" (`bake_material_inputs = DISABLED`, the standing "port the
   * materials, don't bake them" instruction) arrives with near-white stock materials that are
   * useless BY DESIGN. This is how the real ones find their way back on: index `i` names a
   * `kind: 'material'` asset in this same table, and it lines up with the glTF primitive's
   * material index because both come from UE's ordered `static_materials` array.
   *
   * ⚠️ WHY AN ARRAY AND NOT A NAME MAP. The obvious spelling is `{ "Trim": "mat-tech-blue" }`
   * keyed by the glTF material NAME, and it is what the Axon port does. It does not survive a
   * level: UE slot names are not unique across 177 meshes, they are not stable across a re-export,
   * and glTF names are exactly the brittle thing the owner asked to stop depending on. The INDEX
   * is structural — it is the primitive's own `material` field.
   *
   * `.optional()` with NO `.default()`, for `collision`'s reason exactly: `worldHash` hashes the
   * parsed world and `canonicalJson` drops `undefined`, so absent costs no existing world its
   * baked atlas. Absent, or a `null` entry, means "keep whatever the GLB shipped with" — which is
   * the honest answer for an asset nobody has ported materials for yet.
   */
  materials: z.array(z.string().nullable()).optional(),
});
export type WorldFileAsset = z.infer<typeof WorldFileAssetSchema>;

/**
 * Material world asset (rendering plan §1.2): DATA REFERENCING A FACTORY, never shader code —
 * an archetype id + a plain-parameter set + texture-slot references into this world's texture
 * assets. Resolved to a live NodeMaterial by `src/render/materials/catalog.ts` (memoized per
 * id). The archetype enum mirrors src/render/materials/surfaceFactory.ts (R1 set; Physical
 * archetypes land tier-gated in R2). `roughness`/`metalness` default 1.0 in the factory —
 * multipliers over the ORM map, per glTF semantics.
 */
export const WorldMaterialAssetSchema = z.object({
  id: AssetId,
  kind: z.literal('material'),
  archetype: z.enum(['surf.opaque', 'surf.alphaTest', 'surf.emissive', 'surf.unlit']),
  params: z
    .object({
      /**
       * ⭐ A HEX/CSS STRING **OR** A LINEAR `[r, g, b]` TRIPLE (M3).
       *
       * The string form is authored colour and is decoded from sRGB. The triple is the form a
       * PORTED material arrives in: UE's vector parameters are already in working space, and
       * `MI_Martian_Tech_Paint_Blue`'s Tint of (0.412, 0.930, 1.0) written as a hex would be
       * decoded from sRGB a second time and land near (0.14, 0.85, 1.0) — a plausible blue that
       * is not the artist's, across a whole colourway family.
       *
       * This mirrors `surfaceFactory.ColourValue`, and `toColour` is the one fold of the two
       * spellings. Additive: no existing world authors a triple, so no `worldHash` moves.
       */
      color: z.union([z.string(), z.tuple([z.number(), z.number(), z.number()])]).optional(),
      roughness: z.number().min(0).max(1).optional(),
      metalness: z.number().min(0).max(1).optional(),
      emissive: z.union([z.string(), z.tuple([z.number(), z.number(), z.number()])]).optional(),
      emissiveIntensity: z.number().min(0).optional(),
      /** surf.alphaTest only; non-zero (zero↔non-zero is a program fork — plan §1.1 rule 1). */
      alphaTest: z.number().positive().max(1).optional(),
    })
    .optional(),
  /** Slot → texture-asset id (this world's assets table). Unresolved until the A4 loader. */
  textures: z
    .object({
      map: AssetId.optional(),
      normalMap: AssetId.optional(),
      orm: AssetId.optional(),
      emissiveMap: AssetId.optional(),
    })
    .optional(),
  description: z.string().optional(),
});
export type WorldMaterialAsset = z.infer<typeof WorldMaterialAssetSchema>;

/** One world-scoped asset: a file-backed entry, or a factory-referencing material entry. */
export const WorldAssetSchema = z.discriminatedUnion('kind', [
  WorldFileAssetSchema,
  WorldMaterialAssetSchema,
]);
export type WorldAsset = z.infer<typeof WorldAssetSchema>;

/* ───────────────────────────────────────────────────────────────────────────────────────────────
 * LIGHTING IS WORLD DATA (G4), and this block exists because it was not.
 *
 * `scene.ts` carried `sun.position.set(12, 18, 8)` and `DirectionalLight(0xfff2df, 2.4)` as
 * literals. Both were posed for the gym; `sponza-debug-01` places its glTF at `yawDeg: 90`, so the
 * stress rig sits rotated 90° underneath a sun aimed at a different level. G3.1 measured the
 * consequence — **42× the sun bought 3.7× the light**, because the response saturates on the
 * fragments the sun happens to graze while the rest of the scene never sees it at all. That is not
 * an intensity that needs raising; it is a lighting rig belonging to one level and inherited by
 * every other one, with nothing anywhere able to say so.
 *
 * A directional light's pose and colour, a sky's turbidity, and the strength of the constant fill
 * behind them are ART DECISIONS PER WORLD. So they are data, they are validated here, and **every
 * default below reproduces exactly what `scene.ts` hard-coded** — a world that says nothing about
 * lighting is lit today's way, byte for byte (`tests/renderSky.test.ts` pins that, including the
 * polar form of `(12, 18, 8)`).
 *
 * Colours are `#rrggbb` strings — the same vocabulary `WorldMaterialAssetSchema.params.color`
 * already uses — so a world file never carries a bare integer whose colour space is a guess.
 */

/** `#rgb` / `#rrggbb`, the colour vocabulary already used by world material assets. */
const HexColor = z
  .string()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'colour must be #rgb or #rrggbb');

/**
 * THE DIRECT TERM. One shadow-casting directional light, posed in COMPASS ANGLES rather than as a
 * position vector: `azimuthDeg` 0 = +Z and 90 = +X, `elevationDeg` 0 = horizon and 90 = overhead.
 * `src/render/sky.ts`'s `sunPositionFromAngles` is the one converter, and the sky dome's
 * `sunPosition` uniform is driven from the same call, so the light and the sky cannot disagree.
 *
 * The defaults are `(12, 18, 8)` in polar form and `0xfff2df` at 2.4 — i.e. the gym's shipped rig,
 * unchanged. `distanceM` does not affect a directional light's direction; it places the shadow
 * camera, which is why it is data (a sun 23 m from the origin puts half of an 80 m level BEHIND the
 * shadow camera's near plane).
 */
export const WorldSunSchema = z.object({
  /** Compass bearing, degrees: 0 = +Z, 90 = +X. Default = the gym's `(12, 18, 8)`. */
  azimuthDeg: z.number().default(56.309932474020215),
  /** Degrees above the horizon. Default = the gym's `(12, 18, 8)`. */
  elevationDeg: z.number().min(-90).max(90).default(51.29711948164408),
  /** Distance the light is posed at — shadow-camera placement only. Default = |(12, 18, 8)|. */
  distanceM: z.number().positive().default(23.06512518934159),
  /** Warm daylight, ~5600 K. `#fff2df` is the shipped literal. */
  color: HexColor.default('#fff2df'),
  /**
   * Irradiance, in three's light units: `AnalyticLightNode.update()` sets
   * `color × intensity` and the standard BRDF applies `dotNL · that · albedo/π`. So a surface
   * facing the sun receives `intensity`, and a horizontal surface receives
   * `intensity · sin(elevationDeg)` — see `sunHorizontalIrradiance`.
   */
  intensity: z.number().min(0).default(2.4),
  castShadow: z.boolean().default(true),
  /** Half-width of the orthographic shadow camera, metres. The shipped literal is 30. */
  shadowExtentM: z.number().positive().default(30),
  shadowMapSize: z
    .union([z.literal(512), z.literal(1024), z.literal(2048), z.literal(4096)])
    .default(2048),
  /*
   * ── ⭐ B3 · THE SUN'S CASCADES, AUTHORED WITHOUT MOVING A HASH ──────────────────────────────
   *
   * Both `.optional()` and NEITHER `.default()`ed, on purpose: this block is `prefault({})` and
   * every materialised field is hashed into `worldHash`, which `checkProvenance` refuses atlases
   * on. An absent optional key stays absent from `canonicalJson`, so all six committed worlds and
   * the owner's 46,149-capture Sponza atlas keep their hashes (`tests/renderCsm.test.ts` pins
   * it). The tier's row (`lightModel.SUN_SHADOW_TIERS`) is the answer when the world says
   * nothing; `0` ALSO means "the tier decides", because the inspector's number widget has no
   * "absent" and refusing 0 would leave an artist unable to hand the choice back.
   */
  /** Shadow cascades on the sun, 1–4. 0/absent ⇒ the tier's row. `lightModel.sunShadowPolicy`. */
  cascades: z.number().int().min(0).max(4).optional(),
  /** How far from the camera the live sun shadow reaches, metres. 0/absent ⇒ the tier's row. */
  shadowDistanceM: z.number().min(0).max(1000).optional(),
  /*
   * ── ⭐ B6 · THE SUN'S SHADOW UPDATE POLICY — THE ONE LIGHT THE LEVER HAD BEEN LEFT OFF ────────
   *
   * `WorldLightSchema.shadowUpdate` has existed since L2 and the sun did not have it, so the light
   * that owns the CSM cascades — the most expensive shadow in every world, THREE full
   * `renderer.render()` traversals at tier 2 against a 45 ms CPU frame on `mars-hangar-01` — was
   * the one light that could not be scheduled. Same enum, same meanings, same resolver
   * (`lightModel.effectiveShadowUpdate`), same applier (`worldLights.applyShadowUpdatePolicy`).
   *
   * ⚠️ `.optional()` WITH NO DEFAULT, AND THE ONE-WORD DIFFERENCE FROM `WorldLightSchema` IS WHY.
   * `lights` is `.optional()`, so a `.default()` on a per-light field materialises for NO world
   * that authors no light. This block is `prefault({})` — every field of it materialises into
   * EVERY parse — and `worldHash` hashes the whole parsed world, so a `.default('everyFrame')`
   * here would add a key to all six committed worlds, move all six hashes and orphan every baked
   * GI atlas on disk, including the owner's real 46,149-capture `sponza-debug-01/gi/probes.json`.
   * `canonicalJson` drops `undefined`, so an ABSENT optional key stays absent and no hash moves —
   * verified the same way `cascades`/`shadowDistanceM` above were, by the pins in
   * `tests/renderLightRig.test.ts` and `tests/renderCsm.test.ts`.
   *
   * ⛔ AND ABSENT MEANS TODAY'S BEHAVIOUR EXACTLY: `everyFrame`. No world's authored default moves
   * to `staticOnly` in this change. That arm points the shadow camera at
   * `FWG_STATIC_SHADOW_LAYER`, so the player and the companion stop casting a sun shadow at all —
   * a visual trade that belongs to the owner, not to the change that makes it possible.
   *
   * ⛔ THE FOURTH ARM IS UNREPRESENTABLE, NOT REFUSED. `APPLIED_SHADOW_UPDATES` is
   * `SHADOW_UPDATES` minus `'budgeted'`; a budgeted sun would be competing for the traversals left
   * over by itself (`localShadowBudget` = the tier's budget MINUS the sun). See the constant.
   */
  shadowUpdate: z.enum(APPLIED_SHADOW_UPDATES).optional(),
  /**
   * N for `shadowUpdate: 'everyNFrames'`, ignored by the other two arms.
   *
   * `0`/absent ⇒ `DEFAULT_SHADOW_UPDATE_N`. `.min(0)` rather than the per-light row's `.min(2)`
   * for the reason `cascades` is `.min(0)`: the field must be `.optional()` (above), the
   * inspector's widget has no "absent", and refusing 0 would leave an artist unable to hand the
   * choice back. `lightModel.normalizeShadowUpdateN` is the ONE reader and clamps 1 up to 2 —
   * N = 1 is `everyFrame` spelled a second way. `.max(60)` because a shadow that lags a second is
   * not a shadow, it is a stain.
   */
  shadowUpdateN: z.number().int().min(0).max(60).optional(),
});
export type WorldSunConfig = z.infer<typeof WorldSunSchema>;

/**
 * THE SKY, AS IN-SCENE GEOMETRY (`src/render/sky.ts`). Preetham parameters on three's `SkyMesh`.
 *
 * ⚠️ `enabled` DEFAULTS TO FALSE, and that is the "no shipping world changes appearance" fence:
 * a dome is a visible background and a bake-time emitter, so it arrives only where a world asks
 * for one. `sandbox-01`, `mars-web-01`, `playground-01`, `route-test-01` and `axon-spike-01` all
 * say nothing here and are therefore untouched.
 *
 * `cloudCoverage` defaults to **0**, not `SkyMesh`'s own 0.4: the cloud layer is driven by the TSL
 * `time` node, which would make every bake and every screenshot a function of wall-clock time.
 */
export const WorldSkySchema = z.object({
  enabled: z.boolean().default(false),
  /**
   * Uniform scale of the dome in metres. Must stay inside the camera's far plane or the corners
   * clip — `src/render/sky.ts` derives the ceiling (`SKY_SCALE_MAX_M`) and clamps with a warning.
   */
  scaleM: z.number().positive().default(400),
  /** Atmospheric haze. 2 = a clean day; higher is hazier, whiter and dimmer at the zenith. */
  turbidity: z.number().min(0).default(2),
  /** Rayleigh scattering scale — the blue. Lower is a paler, higher-altitude sky. */
  rayleigh: z.number().min(0).default(1),
  mieCoefficient: z.number().min(0).default(0.005),
  mieDirectionalG: z.number().min(0).max(1).default(0.8),
  /** 0 disables the animated cloud layer entirely (see the note above). */
  cloudCoverage: z.number().min(0).max(1).default(0),
});
export type WorldSkyConfig = z.infer<typeof WorldSkySchema>;

/**
 * THE CONSTANT FILL — the fallback implementation of the indirect term, and only ever that.
 *
 * `intensity` here is the BASE the world would run with when nothing else carries the indirect
 * term. It is not what the light ends up holding: `resolveIndirect()` (`src/render/indirect.ts`)
 * remains the only writer of `hemi.intensity`, and it drops this to a residual as soon as a probe
 * volume is present — to a smaller one again when that volume has a real sky baked into it.
 */
export const WorldAmbientSchema = z.object({
  /** Upper hemisphere — cool, ~7000 K daylight. `#cfe0ec` is the shipped literal. */
  skyColor: HexColor.default('#cfe0ec'),
  /** Lower hemisphere — warm dark ground bounce. `#3a3a30` is the shipped literal. */
  groundColor: HexColor.default('#3a3a30'),
  /** Base fill when nothing else carries the indirect term. Default = `AMBIENT_FILL_BASE`. */
  intensity: z.number().min(0).default(AMBIENT_FILL_BASE),
});
export type WorldAmbientConfig = z.infer<typeof WorldAmbientSchema>;

/* ───────────────────────────────────────────────────────────────────────────────────────────────
 * ── ⭐ THE WORLD'S OTHER LIGHTS (E2) — A LIST BESIDE THE SUN, NOT A REGISTRY ENTITY ─────────────
 *
 * `sun`/`sky`/`ambient` are the world's ONE of each. This is the list: an artist's point, spot and
 * extra directional lights, with UE-style MOBILITY. The vocabulary and the (type × mobility)
 * support table live in `src/render/lights/lightModel.ts` — PURE, imported here rather than
 * re-typed, for exactly the reason `AMBIENT_FILL_BASE` and `MAX_LEVEL` are imported above: a
 * second copy of a rule is how two answers to one question begin.
 *
 * Why a light is DATA here rather than an entity class is argued in full at `lightModel.ts`'s
 * header (three reasons, all of them about WHEN the light exists). The short form: the bake and
 * `probeFile.liveLighting()` both need the light set complete and synchronous at scene
 * construction, and ADR-0001 entity classes are neither.
 *
 * ⚠️ `lights` IS `.optional()`, NOT `.default([])`, AND THAT IS A `worldHash` DECISION.
 * `probeFile.worldHash` hashes the PARSED, schema-defaulted world through `canonicalJson`, which
 * DROPS `undefined` — so an absent `lights` leaves every existing world's hash byte-identical and
 * every baked atlas on disk still valid. A `.default([])` would have materialised `"lights":[]`
 * into every parse and gone `stale` on every atlas in the repo for a feature no shipping world
 * uses. The moment a world DOES author lights the hash moves, and that is correct: a baked static
 * light is IN the atlas, so changing it must invalidate it.
 */

/** One authored light. Everything but `id`/`type` defaults; a default light is a movable point. */
export const WorldLightSchema = z.object({
  /** Stable id — the outliner's row, and it is what a refusal names. Unique within the world. */
  id: z.string().min(1),
  type: z.enum(LIGHT_TYPES),
  /**
   * UE's three mobilities, redefined for a pipeline with NO LIGHTMAPS — see `MOBILITY_NOTE`.
   *
   * ⚠️ `movable`, not `dynamic`, and the divergence from `MobilitySchema` (this file, geometry) is
   * deliberate: that enum describes a BODY the physics steps, this one describes when a LIGHT
   * exists, and UE itself calls the light arm "Movable". Aliasing them would let a box's mobility
   * be assigned to a light and typecheck.
   *
   * DEFAULT `movable`: the arm that needs no bake. A light that defaulted to `static` would
   * VANISH the moment it was placed, which is the worst possible first experience of the feature.
   */
  mobility: z.enum(LIGHT_MOBILITIES).default('movable'),
  /**
   * World position. **point/spot only** — a directional light has no position, only a direction,
   * and `azimuthDeg`/`elevationDeg` are that direction. (`WorldSunSchema` makes the same split:
   * its `distanceM` places the shadow camera and nothing else.)
   */
  pos: Vec3Schema.default([0, 3, 0]),
  /**
   * WHERE THE LIGHT COMES FROM, in compass angles — the SAME vocabulary as `WorldSunSchema`, and
   * through the same one converter (`sunPositionFromAngles`, `src/render/sky.ts`). 0 = +Z, 90 = +X;
   * elevation 0 = horizon, 90 = overhead.
   *
   * ⚠️ CONSEQUENCE, STATED SO IT IS NOT DISCOVERED: because these say where the light comes FROM,
   * a SPOT at `elevationDeg: 45` shines DOWNWARD. Two aiming conventions in one lighting block
   * would be the same failure as two lighting rigs, so the sun's convention wins everywhere.
   */
  azimuthDeg: z.number().default(0),
  elevationDeg: z.number().min(-90).max(90).default(45),
  /** Distance the light is posed at — **directional only**, shadow-camera placement, as the sun. */
  distanceM: z.number().positive().default(30),
  color: HexColor.default('#ffffff'),
  /** three's light units; see `WorldSunSchema.intensity` for what a surface actually receives. */
  intensity: z.number().min(0).default(4),
  /**
   * point/spot: three's `light.distance` (the cutoff radius). directional: the ORTHOGRAPHIC
   * half-extent of its shadow camera, i.e. `WorldSunSchema.shadowExtentM`'s job.
   *
   * ⚠️ `.positive()`, NOT `.min(0)`, and it is a measured trap rather than tidiness:
   * `ClusteredLighting` REQUIRES `light.distance > 0` or the light SILENTLY VANISHES (verified in
   * ClusteredLightsNode's culling maths — `src/render/lighting.ts`, and why `stress.ts` gives every
   * spawned light a finite distance). Zod makes it unrepresentable instead of a comment.
   */
  radiusM: z.number().positive().default(12),
  /** spot only — half-angle of the cone, degrees. */
  coneDeg: z.number().min(1).max(89).default(35),
  penumbra: z.number().min(0).max(1).default(0.4),
  /**
   * DEFAULTS FALSE. Every `true` is a shadow map, a depth pass (~3 ms, `src/render/indirect.ts`)
   * and an entry in `LightsNode.customCacheKey()`. Defaulting it on is how a level acquires thirty
   * shadow casters nobody chose.
   */
  castShadow: z.boolean().default(false),
  shadowMapSize: z.union([z.literal(512), z.literal(1024), z.literal(2048)]).default(1024),
  /*
   * ── ⭐ L2 · THE THREE FIELDS THE RUNTIME INTEGRATION NEEDED ────────────────────────
   *
   * ⚠️ THEY MAY BE `.default()`ed AND `WorldSunSchema`'s MAY NOT, AND THE DIFFERENCE IS ONE WORD.
   * `lights` is `.optional()`, so a world that authors no light materialises NO part of this
   * schema and its `worldHash` cannot move — verified: none of the six committed worlds authors a
   * light, and `tests/renderLightRig.test.ts` pins all six hashes plus the one inside the owner's
   * real 46,149-capture `sponza-debug-01/gi/probes.json`. `sun`/`sky`/`ambient` are `prefault({})`
   * and every field of theirs materialises into every parse — which is why the sun's copy of this
   * policy (B6, `WorldSunSchema.shadowUpdate`) had to be `.optional()` with NO default instead.
   * ⚠️ The distinction is the WORD, not the field: `.default()` here is free because `lights` is
   * absent; `.default()` there would have moved all six committed `worldHash`es.
   */
  /**
   * three's `light.decay` — the distance falloff exponent, 2 = physically correct inverse-square.
   *
   * It has ALWAYS been 2: `worldLights.ts` never set it, so every world light silently rode three's
   * constructor default. Authoring it changes no existing picture and costs nothing per frame —
   * `PointLightNode.update()` writes it to `decayExponentNode`, a `renderGroup` UNIFORM, so it is
   * in the free half of `LIGHT_KEY_FREE_FIELDS` and a drag rebuilds no pipeline.
   *
   * **directional only in the negative sense**: there is no distance term in a directional light's
   * shader at all, so this is HIDDEN on that type rather than ignored (`lightFields`).
   */
  decay: z.number().min(0).max(4).default(2),
  /**
   * ⭐ HOW OFTEN THIS LIGHT'S SHADOW MAP IS RE-RENDERED — the one field here that removes
   * milliseconds instead of counting them. The arms, the measurements and the reason it is ONE
   * enum rather than a caching field plus a refresh field are all at `lightModel.SHADOW_UPDATES`.
   *
   * DEFAULT `everyFrame`: exactly what every light did before this field existed. A default of
   * `staticOnly` would silently delete the player's shadow under every lamp in the level.
   *
   * B4 adds `'budgeted'` — the stationary split: live (every frame, movers included) while this
   * light is one of the nearest the tier can afford after the sun, frozen (static only, 0 ms)
   * otherwise. A fourth ARM of this enum rather than a second field, for the reason the enum
   * exists at all; and on a `movable` light it is inert and the row says so. The default does not
   * move, so no world file and no `worldHash` does.
   */
  shadowUpdate: z.enum(SHADOW_UPDATES).default('everyFrame'),
  /**
   * N for `shadowUpdate: 'everyNFrames'`. Ignored by the other two arms.
   *
   * `.min(2)` because N = 1 is `everyFrame` spelled a second way, and two spellings of one
   * behaviour is how a budget line and a renderer come to disagree. `.max(60)` because a shadow
   * that lags a second is not a shadow, it is a stain.
   */
  shadowUpdateN: z.number().int().min(2).max(60).default(DEFAULT_SHADOW_UPDATE_N),
});
export type WorldLightConfig = z.infer<typeof WorldLightSchema>;

/* ───────────────────────────────────────────────────────────────────────────────────────────────
 * GI VOLUMES ARE WORLD DATA (`axon.probeVolume/2`, spec §1.2), for exactly the reason
 * `WorldSunSchema` is (G4): a probe volume's bounds and density are ART DECISIONS PER WORLD, and
 * `drawnBounds()` — a scene-graph walk that unions every drawn mesh — cannot express "1.2 m in the
 * habitat, 19.2 m over the plain". It is the one seam this spec crosses that was not already
 * crossed: lighting GEOMETRY becomes world data.
 *
 * ⚠️ `enabled` DEFAULTS TO FALSE and `volumes` DEFAULTS TO EMPTY, which is the same "no shipping
 * world changes appearance" fence `WorldSkySchema.enabled` carries. A world that says nothing here
 * gets a single volume SYNTHESISED from `drawnBounds()` at the requested spacing — i.e. exactly
 * today's coverage, through the same compiler, the same bake and the same shader (spec §2.4).
 *
 * ⚠️ It still moves `worldHash` for EVERY world, authored or not: `probeFile.ts` hashes the
 * PARSED, schema-defaulted world, so materialising this block into every parse moves every hash
 * (spec §8.2). That is harmless only because `axon.probeVolume/2` already requires a full re-bake,
 * and it is why this note is here rather than in a commit message.
 */

/** One authored volume. Bounds are WORLD-SPACE AABB corners; the compiler snaps them OUTWARD. */
export const GiVolumeSchema = z.object({
  /** Stable id — appears verbatim in the compiler's refusal and snap messages. */
  id: z.string().min(1),
  min: Vec3Schema,
  max: Vec3Schema,
  /**
   * DENSITY LEVEL, not a metre value: `spacing = baseSpacingM · 2^level`, so 0 is finest and the
   * ladder at the 1.2 m base is 1.2 / 2.4 / 4.8 / 9.6 / 19.2 m.
   *
   * An arbitrary metre value cannot be snapped to a common lattice, and a density boundary that is
   * off the lattice is a seam no border scheme can close (spec §5/M1) — the artist picks a rung
   * precisely so a fine volume's probes land on a SUBSET of the coarse volume's probes rather than
   * merely near them.
   */
  level: z.number().int().min(0).max(MAX_LEVEL),
  /**
   * Expand outward by this many bricks OF THIS VOLUME'S OWN LEVEL before rasterising. Unreal's
   * `VoxelizationCellExpansionForSurfaceGeometry`, and the reason is in its own config comment:
   * dilation does not operate across bricks of different densities, so the transition has to be
   * moved OFF the geometry that motivated the fine volume. A seam in open air has no surface to
   * land on.
   *
   * DEFAULT 2, not 1: one level-0 brick is 4.8 m — smaller than a typical interior room, so a
   * single brick of clearance often leaves the density transition still on the wall. Two is 9.6 m.
   * 0 is legal and is REPORTED in `report.snaps`, never silently accepted.
   */
  expandBricks: z.number().int().min(0).max(8).default(2),
});
export type GiVolumeConfig = z.infer<typeof GiVolumeSchema>;

/** The world's authored GI structure. Compiled to ONE runtime structure by `probeCompile.ts`. */
export const GiSchema = z.object({
  /** Off ⇒ the synthesised single volume, i.e. today's coverage. */
  enabled: z.boolean().default(false),
  /**
   * THE ONE WORLD ORIGIN every lattice is measured from. All levels snap to it, so a coarse probe
   * is COINCIDENT with a fine probe rather than merely near one (spec §1.4/M2). Moving it
   * invalidates every atlas in the world — it is part of `worldHash`, and that is correct.
   */
  origin: Vec3Schema.default([0, 0, 0]),
  /** Level-0 probe spacing, metres. 1.2 is the interior default the owner specified. */
  baseSpacingM: z.number().positive().max(64).default(1.2),
  /**
   * ⭐ THIS WORLD'S PROBE CEILING — the hard refusal's threshold, authorable in DATA.
   *
   * The refusal itself is not in question and does not move: over the ceiling `probeVolume()`
   * THROWS (`ProbeBudgetError`) rather than silently coarsening, because silent coarsening is what
   * made every "1 m bake" in G2–G4 something other than 1 m. What was wrong is that the threshold
   * was a CLI flag (`--max-probes`) and an engine constant, so an artist authoring volumes in the
   * editor met a refusal they had no way to answer in the world they were editing.
   *
   * ⚠️ AND IT IS NOT THE ONLY RAIL. `probeCompile.ts` carries a SECOND, independent budget —
   * `atlasBytes > maxAtlasBytes` — which bounds BRICKS rather than probes, and the two diverge.
   * At 64 B per probe and Sponza's measured 1.784 padding ratio (~114 B per unique capture) the
   * atlas budget binds at roughly 560,000 captures, well below the 1,048,576 that
   * `probeGrid.makeFacingCache` asserts for `maxTextureDimension2D`. Raise this past ~500,000 and
   * the two rails CROSS: the artist stops getting a clear message about probe count and starts
   * getting a confusing one about atlas bytes for the same authoring mistake.
   *
   * `.optional()` with NO `.default()` — the `collision` rule. `worldHash` hashes the parsed world
   * and `canonicalJson` drops `undefined`, so absent costs no existing world its baked atlas.
   */
  maxProbes: z.number().int().positive().optional(),
  volumes: z
    .array(GiVolumeSchema)
    .refine((v) => new Set(v.map((x) => x.id)).size === v.length, 'gi volume ids must be unique')
    .default([]),
  /**
   * Fill every indirection cell inside the grid that no authored volume covers with a real brick
   * (Unity's `fillEmptySpaces`). DEFAULT TRUE, and it closes a whole failure class: with it off an
   * uncovered cell resolves to `EMPTY_BRICK`, whose stored irradiance is exactly zero, so the last
   * real brick's face abuts a hard black step on a 4.8 m grid — worse than v1's clamp-to-edge
   * extrapolation. A coarse brick is cheap: at level 3 one brick is 125 captures for 56,623 m³.
   *
   * FALSE is retained for measurement and for S1's corruption assertion, and it produces that hard
   * boundary BY DESIGN.
   */
  fillEmptySpaces: z.boolean().default(true),
  /**
   * ABSOLUTE normal offset in metres at sample time. NOT spacing-scaled: under a hierarchy the
   * local spacing at the vista level is 19.2 m, and v1's `spacing · 0.5`
   * (`sparseNode.ts`'s `normalBiasM` offset) would push the sample point 9.6 m through the wall.
   * Unity's
   * `normalBias` is 0.05 m absolute with `scaleBiasWithMinProbeDistance` OFF by default, and where
   * it does scale it scales by the FINEST spacing globally, never the local brick's.
   *
   * Absent ⇒ `min(0.5 · baseSpacingM, 0.25)` = 0.25 m at the 1.2 m base.
   * ⚠️ UNVERIFIED. v1's realised offset at a true 1.2 m Sponza volume is 0.6 m, so the default is
   * a 2.4x reduction taken for a CORRECTNESS reason, and it changes leak behaviour. Stage S4 owns
   * the A/B that sets this constant; until that measurement exists every leak number quoted
   * against it is provisional.
   */
  normalBiasM: z.number().min(0).max(1).optional(),
  /** Runtime gain on the volume's contribution — the G2 `renderGroup` lever, and it is real now. */
  intensity: z.number().min(0).default(1),
  /**
   * ── ⭐ SURFACE-ADAPTIVE PLACEMENT (S1 → DEFAULT ON IN B2-sparse) ───────────────────────────────
   *
   * The compiler is handed an occupancy grid voxelised from this world's own drawn geometry and
   * DROPS bricks with no surface within the guard (`probeCompile.ts`, per level and capped).
   * Measured on Sponza's real grid, no bake: 648 → 144 bricks, 46,149 → 10,725 probes, 76.8 %
   * removed, and every one of the 36,752 probes that were more than 2.4 m from anything is gone.
   *
   * ⭐ **ABSENT MEANS ON. `enabled: false` IS THE EXPLICIT UNIFORM ARM.** The default is resolved
   * by `resolveGiAdaptive` below, in code, and NOT by a zod `.default()` — and that is a
   * `worldHash` decision, the same one `lights` took for the same measured reason: `canonicalJson`
   * drops `undefined`, so a world that does not author this keeps the EXACT hash it had before the
   * field existed and **every atlas on every disk stays loadable**. A `.default({enabled:true})`
   * here would move every world's hash, refuse every baked atlas including the owner's 46,149-
   * capture Sponza bake, and make *"bake → see it → reload → still there"* false again on the day
   * he is re-baking. Placement is not identity (`checkProvenance` never reads brick counts); an
   * atlas placed uniformly is a correct, fatter picture of the same light. The header's
   * `bake.placement` block is what records which rule a file was placed under.
   *
   * ⚠️ **AND IT IS NOT A `bakeQuality` PRESET KNOB, EVER.** A preset may change what a bake COSTS;
   * it may not change what the file IS. `spacingMultiplier` and `validity` both broke that rule and
   * both produced technically-valid, practically-useless atlases. Placement is the same class:
   * a preset that changed it would write a file whose STRUCTURE only that preset can reproduce.
   * Fenced in `tests/renderBakeWorkflow.test.ts` over the whole preset table.
   *
   * ⭐ **THE PRECONDITION S1 NAMED IS BUILT (S2).** A dropped cell points at `EMPTY_BRICK`, and
   * `scatterCaptures` writes the world's resolved ambient fill into that slot — so a hole reads as
   * the indirect term the world had before GI existed, never black. That is what makes a default
   * safe: the worst case of "adaptive deleted a brick over geometry" is the pre-GI ambient there,
   * not a black hole, and the panel now says per volume how much of it fell to the fill.
   */
  adaptive: z
    .object({
      /**
       * ⚠️ `.optional()`, NOT `.default(false)`, so that an authored block that SAYS NOTHING about
       * `enabled` (`{ voxelM: 0.6 }`) resolves to the same default an absent block does. Only an
       * explicit `false` is the uniform arm. No shipping world authors this block, so nothing's
       * hash moves; an explicit `true`/`false` hashes exactly as it did.
       */
      enabled: z.boolean().optional(),
      /**
       * Voxel edge for the occupancy voxelisation, metres. Default = one probe spacing.
       * Finer resolves thin walls and sealed pockets better and costs walk time and memory only —
       * it is a BAKE cost and never a runtime one.
       */
      voxelM: z.number().positive().max(16).optional(),
      /**
       * Clearance, metres, around a brick that still counts as "near a surface". Default =
       * `baseSpacingM`, i.e. ONE probe spacing. ABSOLUTE, never scaled by the brick's level —
       * a level-scaled guard is 19.2 m at the vista level and reaches everything.
       */
      surfaceGuardM: z.number().min(0).max(64).optional(),
      /**
       * ── ⭐ VIRTUAL OFFSET (`render/gi/probeOffset.ts`) — OPT IN, PAIRED WITH `voxelM` ──────────
       *
       * Relocate a probe that landed INSIDE geometry to the nearest clear position within 0.30 m,
       * at COMPILE time, before the cube camera photographs it. The lattice the shader interpolates
       * over is untouched, so this changes no byte of the runtime structure and no shader fetch —
       * it changes where 52,149 cameras stand.
       *
       * ⚠️ `.optional()` and DEFAULT OFF, and it is the one adaptive knob that is not default-on.
       * The reason is not caution, it is arithmetic: `probeOffset.ts` REFUSES an occupancy voxel
       * coarser than `baseSpacingM/4`, because at 1.2 m the nearest position the grid can call air
       * may be 1.8 m away — past half a probe spacing. A default-on offset would therefore make
       * every world that authors no `voxelM` throw on compile. Author the pair:
       *
       *     "adaptive": { "voxelM": 0.3, "virtualOffset": true }
       *
       * ⚠️ IT IS A PLACEMENT RULE, SO IT IS NOT A `bakeQuality` PRESET KNOB, EVER — the same fence
       * the block above carries. A preset may change what a bake COSTS; it may not change where the
       * probes are.
       */
      virtualOffset: z.boolean().optional(),
    })
    .optional(),
});
export type GiConfig = z.infer<typeof GiSchema>;

/**
 * ── ⭐ "WHICH VOLUMES DOES THIS WORLD AUTHOR?" — ONE ANSWER, FOR BOTH ENDS OF THE BAKE ──────────
 *
 * `enabled` is the gate (it defaults false, the same fence `WorldSkySchema.enabled` carries), so a
 * world that authors volumes but has not switched them on gets today's coverage: the caller
 * synthesises one volume from the drawn bounds (spec §2.4) and the header records `[]`.
 *
 * It is a function here, in the schema module, rather than an expression at each site, because
 * FOUR places have to agree on it and they are in three processes: the page's compile, the page's
 * `readProbeAtlas` header, `checkProvenance` at load, and `tools/bundler/bake-world.ts`'s
 * cross-check of the header it gets back. `bake-world.ts` HAD its own copy — it read
 * `gi.volumes` directly — and the first `enabled: false` bake it met therefore reported
 * "authors 2 gi volume(s)" over a page that had correctly synthesised one, and the cross-check
 * refused a bake that was perfectly good. Two answers to one question, which is the same shape as
 * every other failure this subsystem has shipped.
 */
export function authoredGiVolumes(gi: GiConfig): GiVolumeConfig[] {
  return gi.enabled ? gi.volumes : [];
}

/**
 * ── ⭐ "IS THIS WORLD PLACED ADAPTIVELY, AND WITH WHAT?" — ONE ANSWER (B2-sparse) ───────────────
 *
 * The same argument as `authoredGiVolumes`, one field over: FOUR sites in THREE processes have to
 * agree — the page's compile, the page's `giBakePlan()` (the panel's price), the header the page
 * writes (`bake.placement`), and `tools/bundler/bake-world.ts`'s cross-check of that header. The
 * S1 answer lived as an expression inside `scene.ts` (`giAdaptive()`), which is exactly the shape
 * that produced "authors 2 gi volume(s)" over a page that had compiled 1.
 *
 *   `enabled`   — TRUE unless the world writes `adaptive.enabled: false`. Absent, `{}` and
 *                 `{ voxelM }` all resolve to ON. (See the schema note for why this is not a zod
 *                 default: `worldHash` hashes the schema-defaulted world.)
 *   `voxelM`    — the occupancy voxel edge; absent ⇒ one probe spacing (`baseSpacingM`).
 *   `surfaceGuardM` — the LEVEL-0 clearance; absent ⇒ one probe spacing. Coarser levels are
 *                 derived from it inside the compiler (`AdaptiveReport.guardByLevelM`).
 *   `explicit`  — whether the world SAID anything (`adaptive` present at all). Reported so a
 *                 header can distinguish "the default" from "the artist chose this".
 *
 * `baseSpacingM` is a PARAMETER because the two ends compile at different bases: the page at
 * `giCompileSpacing()` (the authored base, or the session's 4 m for a world that authors nothing),
 * the baker at the header's own `structure.baseSpacingM`. Only `enabled` is cross-checked between
 * them; the two metre values are recorded, not compared.
 */
export interface GiAdaptiveResolved {
  enabled: boolean;
  voxelM: number;
  surfaceGuardM: number;
  /**
   * ⭐ Run the virtual-offset pass. **Default FALSE** — the one field here whose absence means off,
   * because `probeOffset.ts` refuses a voxel coarser than `baseSpacingM/4` and a default-on pass
   * would make every world that authors no `voxelM` throw on compile. See the schema note.
   *
   * Reported false whenever `enabled` is false as well: the pass needs the occupancy grid, and the
   * explicit uniform arm produces none. Two fields that can disagree about whether there is a grid
   * is the "authors 2 gi volume(s)" shape this resolver exists to prevent.
   */
  virtualOffset: boolean;
  explicit: boolean;
}

export function resolveGiAdaptive(gi: GiConfig, baseSpacingM: number): GiAdaptiveResolved {
  const cfg = gi.adaptive;
  const enabled = cfg?.enabled !== false;
  return {
    enabled,
    voxelM: cfg?.voxelM ?? baseSpacingM,
    surfaceGuardM: cfg?.surfaceGuardM ?? baseSpacingM,
    virtualOffset: enabled && cfg?.virtualOffset === true,
    explicit: cfg !== undefined,
  };
}

/**
 * ── ⭐ WHICH IMPLEMENTATION OF THE INDIRECT TERM THIS WORLD USES (BUG 1) ────────────────────────
 *
 * `src/render/indirect.ts` holds the law that a constant ambient and an irradiance volume are two
 * implementations of ONE responsibility. This field is where a world states which one it runs.
 *
 *   'fill'  — the constant hemisphere fill. Today's shipped behaviour, and the default, so no
 *             existing world changes appearance (the same fence `sky.enabled` and `gi.enabled`
 *             carry).
 *   'baked' — this world's baked probe volume (`worlds/<id>/gi/`) is part of its CONTENT. The
 *             runtime fetches it, checks provenance, and binds it; every failure falls back to the
 *             fill with a NAMED state (`gi.stale` + the derived `bakeState`), never to a dark room
 *             and never to a silent wrong one.
 *
 * It is a REQUEST, not a fact: `GameScene.gi()` reports what is actually carrying the term and
 * `resolveIndirect()` remains the only arbiter — the same request/fact split as `bake.quality`.
 *
 * ⚠️ IT IS NOT `gi.enabled`, AND THE TWO MUST NOT BE MERGED. `gi.enabled` already means something
 * else and something real — *"compile my AUTHORED volumes rather than one synthesised from the
 * drawn bounds"* (`authoredGiVolumes`, four call sites in three processes). A world with
 * `enabled: false` still bakes a perfectly legitimate, loadable atlas from a synthesised volume.
 * Overloading it would make "I have no authored volumes" and "I have no baked lighting" the same
 * statement, which is the exact `source: 'none'` conflation `bakeState.stale` was invented to undo.
 *
 * ⚠️ IT LIVES HERE, NOT INSIDE `gi`, because `lighting.gi` is the BAKE RECIPE — inputs to a build
 * step — and this is a RUNTIME decision. A world may carry a full recipe and still run on the fill
 * (that is what an unbaked level is), and may carry no recipe at all and still run baked (the
 * synthesised-volume case).
 *
 * ⚠️ AN ENUM, NOT A BOOLEAN, and the reason is named now so nobody "simplifies" it: the editor's
 * UE-style per-light mobility asks for static / stationary / movable, and "baked indirect +
 * dynamic direct" is a third arm this field will grow. A boolean would have to be replaced.
 */
export const WorldIndirectSchema = z.enum(['fill', 'baked']);
export type WorldIndirectMode = z.infer<typeof WorldIndirectSchema>;

/** A world's whole lighting rig. Every sub-block defaults, so `{}` is today's shipped scene. */
/**
 * ── ⭐ FOG, AND IT WAS TWO HARD-CODED NUMBERS UNTIL NOW ─────────────────────────────────────────
 *
 * `scene.ts` built `Fog(0x1a2836, 60, 150)` unconditionally, for every world, with no way to see
 * it or change it. The owner spent a session diagnosing a "fade to completely black" that WAS this:
 * a near-black blue-grey saturating at 150 m, on a level whose cascades end at 90 m — so the band
 * where he was hunting a shadow bug was in fact the band where fog took over.
 *
 * ⚠️ `.optional()` ON THE LIGHTING BLOCK, NOT `.default()`, AND THAT IS A `worldHash` DECISION —
 * the same one `lights` makes and for the same reason. `canonicalJson` drops `undefined`, so a
 * world that authors no fog keeps the EXACT hash it had before this field existed and every atlas
 * on every disk stays valid. The defaults below REPRODUCE today's hard-coded values byte for byte,
 * so an absent block and the old code are the same picture.
 *
 * This is the artist-facing knob, NOT the volumetric fog component the owner asked for — that is a
 * subsystem (tiers, probe lighting, a height-fog fallback) and it is not this. What this buys is
 * the ability to TURN THE THING OFF and to tune it per world while that is built.
 */
export const WorldFogSchema = z.object({
  /** `false` ⇒ `scene.fog = null`. The escape hatch the hard-coded version never had. */
  enabled: z.boolean().default(true),
  /** Linear fog colour. `#1a2836` is the shipped literal — a dark blue-grey, near-black at depth. */
  color: HexColor.default('#1a2836'),
  /** Metres at which fog begins. 60 is the shipped literal. */
  nearM: z.number().min(0).default(60),
  /** Metres at which fog is total. 150 is the shipped literal. */
  farM: z.number().positive().default(150),
  /*
   * ── ⭐ F1 · WHICH IMPLEMENTATION OF THE ONE TERM ────────────────────────────────────────────
   *
   * The owner asked for a volumetric fog component "similar to UE's". `render/fog/fogModel.ts`
   * holds the argument for why that is a MODE on this block and not a second component: fog
   * already has one config, one writer (`scene.applyFog`) and one schema, and a second authority
   * beside them is precisely the "two competing values/systems" he objected to in the shadow
   * panel. So the subsystem grows as rows in a table rather than as a parallel block.
   *
   * ⛔ `volumetric` IS NOT IN THIS ENUM and must not be added before its implementation exists —
   * `FADE_DISTANCE_REFUSAL`'s ruling for lights, applied here. A mode that renders as something
   * else is unfalsifiable on a term whose whole output is a look.
   *
   * ⚠️ `.default('linear')` IS A `worldHash` DECISION, LIKE EVERY DEFAULT IN THIS BLOCK. The block
   * itself is `.optional()` on `WorldLightingSchema`, `canonicalJson` drops `undefined`, and no
   * committed world authors a `fog` key — so these three new fields materialise into NOTHING and
   * all six pinned `worldHash`es stand (`tests/renderLightRig.test.ts` runs that fence first). The
   * owner's live Sponza atlas cannot be staled by this growth. It IS staled by his first fog EDIT,
   * which is stated to him in the panel rather than engineered around — an allowlist inside the
   * hash is a second answer to "is this atlas of this world" (`WorldLightingSchema.indirect`).
   */
  mode: z.enum(['linear', 'height']).default('linear'),
  /**
   * Height-fog density, in **1/m²** — NOT per metre; the name would have to be `densityPerM2` to
   * say so and that reads worse than the note. `fogModel.HEIGHT_DENSITY_NOTE` carries the
   * arithmetic and the worked example that produced this default: `factor = 1 − exp(−(d·m)²)` with
   * `m = (top − worldY) · viewZ`, so `0.00025` under a 40 m top is ~63 % fog at 100 m — the same
   * saturation distance the shipped linear default (60→150 m) reaches at ~118 m.
   *
   * The ceiling is a guard rail, not a tuning opinion: at `0.01` the frame is solid fog within
   * ~2.5 m of any surface below the top, which is the "everything went grey and I cannot undo what
   * I typed" shape. Unused in `linear` mode and HIDDEN there (`fogFields`).
   */
  density: z.number().min(0).max(0.01).default(0.00025),
  /**
   * World-space Y above which there is NO fog at all. Metres, and it may legitimately be negative
   * (a level authored below the origin), so no `.min()`.
   */
  topM: z.number().default(40),
});
export type WorldFogConfig = z.infer<typeof WorldFogSchema>;

/**
 * ── ⭐ WHAT AN ABSENT `lighting.fog` BLOCK MEANS, AND IT MEANS **NO FOG** ───────────────────────
 *
 * The owner, on the level that authors no fog block: *"when we move the camera far away from a
 * mesh, it still does turn black … it is a local fog fade that we added (that shouldn't be in the
 * framework as we handle fog a different way)."* He was right, and this function is the ruling.
 *
 * ⛔ **THIS IS THE FORCED GROUND PLANE AGAIN.** *"We can't have a forced ground plane like that in
 * our levels"* retired a 240 m quad the framework drew into every world; an absent `fog` block
 * resolving to `Fog(0x1a2836, 60, 150)` is the same defect in the same class — engine furniture a
 * level never asked for, dressed as a default. It was total at 150 m in a colour that is near-black,
 * so **every world in the repo faded to black at distance and only one of them had said so.**
 *
 * ⚠️ **AND IT WAS SPELLED FIVE TIMES, WHICH IS WHY IT SURVIVED THE FOG COMPONENT LANDING.** F1 gave
 * fog a schema, one writer and a Details panel and left the ANSWER TO "what does absent mean"
 * duplicated across `scene.applyFog`, `outline`'s row detail, `detailsModel`, the editor's write
 * funnel and `lightModel.describeShadowReach` — five readers that agreed with each other, so no
 * test could catch that they agreed on the wrong thing. **This is the one answer; every reader
 * calls it.** `lightModel.ts` may not import this file (it is imported BY it, the hard constraint
 * in its own header) and keeps a second copy made safe by `tests/renderFogModel.test.ts`.
 *
 * ⭐ **ABSENT KEEPS THE DEFAULTS AND TURNS THEM OFF, RATHER THAN RETURNING NOTHING.** The panel has
 * to draw a value for `color`/`nearM`/`farM` before any block exists, and an artist ticking
 * `enabled` wants the shipped literal as their starting point — that literal is a good fog, it was
 * simply never anybody's choice. So absence is *"this world has fog available and switched off"*,
 * which is what the outliner row, the Details rack and the frame all now say in unison.
 *
 * ⚠️ **NO `worldHash` MOVES AND NO SCHEMA FIELD CHANGED.** `WorldFogSchema`'s own defaults are
 * untouched — a MATERIALISED block still defaults `enabled: true`, because writing the block IS the
 * statement that the world wants fog. Only the meaning of ABSENCE moved, and absence is not in the
 * hash: `lighting.fog` is `.optional()` and `canonicalJson` drops `undefined`. The owner's live
 * Sponza atlas is untouched by this.
 */
export function resolveFogConfig(fog: WorldFogConfig | undefined): WorldFogConfig {
  if (fog !== undefined) return fog;
  return { ...WorldFogSchema.parse({}), enabled: false };
}

export const WorldLightingSchema = z.object({
  // `prefault`, not `default`: zod 4's `.default()` takes the OUTPUT type, and `{}` is not a
  // fully-populated lighting block. `prefault` feeds `{}` through the schema so every inner
  // default applies — which is the whole point (an absent block must mean "today's rig").
  sun: WorldSunSchema.prefault({}),
  sky: WorldSkySchema.prefault({}),
  ambient: WorldAmbientSchema.prefault({}),
  /**
   * ABSENT means "today's fog", not "no fog" — see `WorldFogSchema`. Optional so no committed
   * `worldHash` moves and no baked atlas is orphaned by this field existing.
   */
  fog: WorldFogSchema.optional(),
  /**
   * ⭐ THE WORLD'S OTHER LIGHTS (E2) — see `WorldLightSchema`. ABSENT by default, and absent means
   * absent: `canonicalJson` drops `undefined`, so a world that authors no lights keeps the exact
   * `worldHash` it had before this field existed and its baked atlas stays valid. That is the
   * whole reason this is `.optional()` rather than `.default([])`.
   */
  lights: z
    .array(WorldLightSchema)
    .refine((v) => new Set(v.map((x) => x.id)).size === v.length, 'light ids must be unique')
    .optional(),
  /** The authored GI structure (`axon.probeVolume/2`). Empty + disabled ⇒ today's coverage. */
  gi: GiSchema.prefault({}),
  /**
   * Which implementation carries this world's indirect term at runtime — see
   * `WorldIndirectSchema` above. Defaults to the constant fill, so no existing world moves.
   *
   * ⚠️ ADDING IT MOVED `worldHash` FOR EVERY WORLD (`probeFile.worldHash` hashes the PARSED,
   * schema-defaulted world), so every atlas baked before this line went `stale` on the first boot
   * after it landed. That cost is paid deliberately and batched with the `medium` preset's
   * validity/dilation fix, which requires the same re-bake anyway — one bake, both fixes. Do NOT
   * carve a per-field exception into `worldHash` to avoid it: an allowlist inside the hash is a
   * second answer to "is this atlas of this world", and second answers are this subsystem's whole
   * failure history.
   */
  indirect: WorldIndirectSchema.default('fill'),
});
export type WorldLightingConfig = z.infer<typeof WorldLightingSchema>;

export const WorldSchema = z.object({
  schema: z.literal('axon.world/1'),
  worldId: z.string(),
  name: z.string(),
  spawn: Vec3Schema,
  /** Entity class names the runtime must have registered — the ADR-0001 capability gate. */
  requires: z.array(z.string()).default([]),
  /** World-level extensions (ADR-0002 "prefabs") — pure data, resolved at load. */
  prefabs: z.record(z.string(), WorldPrefabSchema).default({}),
  /**
   * World-scoped assets (ADR-0002 tier c) — manifest only: parsed + browsable (editor WORLD
   * ASSETS root), NOT loaded. The bundle pipeline (A4) produces and consumes these entries.
   */
  assets: z
    .array(WorldAssetSchema)
    .refine((list) => new Set(list.map((a) => a.id)).size === list.length, {
      message: 'asset ids must be unique within the world',
    })
    .default([]),
  /** Optional surface-palette override, merged over content/tuning/surfaces.json. */
  surfaces: z.record(z.string(), SurfaceOverrideSchema).optional(),
  /**
   * The world's lighting rig (G4) — sun pose/colour/intensity, sky, constant fill. Defaulted, and
   * every default reproduces the literals `scene.ts` used to carry, so an existing world file is
   * lit exactly as before.
   */
  lighting: WorldLightingSchema.prefault({}),
  boxes: z.array(WorldBoxSchema),
  platforms: z.array(WorldPlatformSchema),
  /** Validated per-entry by the entity registry, not here. */
  entities: z.array(z.unknown()),
});

export type WorldData = z.infer<typeof WorldSchema>;

// Compile-time lock: the world schema's geometry output must stay assignable to the runtime
// types (physics/scene consume world.boxes/world.platforms as GymBox/PlatformDef) and the gym
// generators' output must stay assignable to the schema input — either drifting breaks here.
type AssertTrue<T extends true> = T;
type _BoxOut = AssertTrue<z.infer<typeof WorldBoxSchema> extends GymBox ? true : false>;
type _BoxIn = AssertTrue<GymBox extends z.input<typeof WorldBoxSchema> ? true : false>;
type _PlatOut = AssertTrue<z.infer<typeof WorldPlatformSchema> extends PlatformDef ? true : false>;
type _PlatIn = AssertTrue<PlatformDef extends z.input<typeof WorldPlatformSchema> ? true : false>;

export function parseWorld(raw: unknown): WorldData {
  return WorldSchema.parse(raw);
}

/**
 * ADR-0001 capability gate: every class the world `requires` must be registered in this runtime.
 * The caller refuses loudly on `!ok` — an old runtime must never half-load a newer world.
 */
export function checkRequires(
  world: WorldData,
  available: string[],
): { ok: boolean; missing: string[] } {
  const have = new Set(available);
  const missing = world.requires.filter((name) => !have.has(name));
  return { ok: missing.length === 0, missing };
}

/**
 * Merge a world's surface-palette override over the content palette. Overridden fields win,
 * untouched fields (and untouched tags) keep their content values, and tags only the world knows
 * become full profiles with schema defaults applied.
 */
export function mergeSurfaces(
  base: SurfaceMap,
  overrides?: Record<string, Partial<SurfaceProfile>>,
): SurfaceMap {
  if (!overrides) return base;
  const out: SurfaceMap = { ...base };
  for (const [tag, over] of Object.entries(overrides)) {
    const existing = out[tag];
    if (existing) {
      const merged = { ...existing };
      if (over.frictionMul !== undefined) merged.frictionMul = over.frictionMul;
      if (over.maxSpeedMul !== undefined) merged.maxSpeedMul = over.maxSpeedMul;
      if (over.cadenceMul !== undefined) merged.cadenceMul = over.cadenceMul;
      out[tag] = merged;
    } else {
      out[tag] = SurfaceProfileSchema.parse(over);
    }
  }
  return out;
}
